import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit, computed, inject, input, signal } from '@angular/core';

import { IAggregatedPrice, IMarket, IOutcome, IPriceCore, ISportEvent } from '@frontend/oxygen/shared-utils';

import { BetslipBetDataUtils } from '../../../betslip/models/betslip-bet-data.utils';
import { IBetSelection } from '../../../betslip/services/betSelection/bet-selection.model';
import { BetslipService } from '../../../betslip/services/betslip/betslip.service';
import { BetslipSelectionsDataService } from '../../../core/services/betslipSelectionsData/betslip-selections-data';
import { PubSubService } from '../../../core/services/communication/pubsub/pubsub.service';
import { DeviceService } from '../../../core/services/device/device.service';
import { GtmTrackingService } from '../../../core/services/gtmTracking/gtm-tracking.service';
import { IGtmOrigin } from '../../../core/services/gtmTracking/models/gtm-origin.model';
import { SessionStorageService } from '../../../core/services/storage/session-storage.service';
import { StorageService } from '../../../core/services/storage/storage.service';
import { UserService } from '../../../core/services/user/user.service';
import { WindowRefService } from '../../../core/services/windowRef/window-ref.service';
import { EventService } from '../../../sb/services/event/event.service';
import { RoutingState } from '../../../shared/services/routingState/routing-state.service';
import { GA_CONSTANTS, Initiator, PcbbHomepageAddButtons } from '../models/pre-created-bet-builder.constants';
import { IAddToBetSlipGTMObject, IHandicap, IPreCreatedAddToBetSlipEventData } from '../models/pre-created-bet-builder.model';

@Component({
    selector: 'pre-created-add-to-betslip',
    templateUrl: 'pre-created-add-to-betslip.component.html',
    styleUrls: ['pre-created-add-to-betslip.component.scss'],
    standalone: true,
    imports: [CommonModule],
})
export class PreCreatedAddToBetslipComponent implements OnInit, OnDestroy {
    eventRefSignal = input<ISportEvent>();
    hashIdSignal = input<string>('');
    priceSignal = input<IAggregatedPrice>();
    isSuspendedSignal = input<boolean>();
    isDesktop: boolean;

    preCreatedAccasBetsData: IPreCreatedAddToBetSlipEventData[];

    @Input() markets: IMarket[];
    @Input() isLadbrokes: boolean;

    @Input() initiator?: string;
    @Input() eventIndex: number;
    betState: boolean;

    readonly addToBetslip: string = PcbbHomepageAddButtons.ADDTOBETSLIP;
    readonly addedToBetslip: string = PcbbHomepageAddButtons.ADDEDTOBETSLIP;
    readonly atTheRate: string = PcbbHomepageAddButtons.ATTHERATE;
    readonly fraction: string = PcbbHomepageAddButtons.FRACTION;
    readonly FEATUREDMODULE: string = Initiator.FEATURED_MODULE;

    timer: number;
    isSelected = signal<boolean>(false);

    formattedPrice = computed(() => {
        if (this.isSuspendedSignal()) {
            return 'SUSP';
        }
        const price = this.priceSignal();
        if (!price) return '';

        return this.user.oddsFormat === 'frac' ? `${price.priceNum}/${price.priceDen}` : price?.decimal?.toFixed(2);
    });

    marketsData: IMarket[] = [];
    pubSubService = inject(PubSubService);
    storageService = inject(StorageService);
    betslipService = inject(BetslipService);
    sessionStorage = inject(SessionStorageService);
    gtmTrackingService = inject(GtmTrackingService);
    windowRef = inject(WindowRefService);
    routingState = inject(RoutingState);
    betslipSelectionsDataService = inject(BetslipSelectionsDataService);
    eventService = inject(EventService);
    deviceService = inject(DeviceService);
    user = inject(UserService);
    changeDet = inject(ChangeDetectorRef);

    ngOnInit(): void {
        this.isDesktop = this.deviceService.isDesktop;
        this.isHomeSLPButtonSelected(this.hashIdSignal());
        this.checkSelectedState();
        this.pubSubService.subscribe(
            `preCreatedCard-${this.hashIdSignal()}`,
            [this.pubSubService.API.ADDTOBETSLIP_PROCESS_FINISHED, this.pubSubService.API.BET_PLACED, this.pubSubService.API.SYNC_PRECREATED_DATA],
            () => {
                this.checkSelectedState();
                this.isHomeSLPButtonSelected(this.hashIdSignal());
                this.changeDet.detectChanges();
            },
        );
    }

    /**
     * Checks if the current hash ID is selected by comparing it with stored hash IDs.
     *
     */
    private checkSelectedState(): void {
        const bbHashIds: string[] = this.storageService.get('bbHashIds');
        this.isSelected.set(!!bbHashIds?.includes(this.hashIdSignal()));
    }

    /**
     * Enables/disables button for PCBB home and SLP pages
     * @param hashId
     * @returns boolean
     */
    isHomeSLPButtonSelected(hashId: string): boolean {
        const bbHashIds: string[] = this.storageService.get('bbHashIds');
        return bbHashIds?.includes(hashId);
    }
    /**
     * method for Check whether other bet types are exist in the betslip
     *
     */
    public accaBarClick(): void {
        let storedSelections = this.storageService.get('toteBet');
        storedSelections = storedSelections ? [storedSelections] : this.storageService.get('betSelections');
        const otherBetsExist = storedSelections && storedSelections.some((res: IBetSelection) => res.isLotto || res.isTote);
        if (otherBetsExist) {
            this.betslipService.showBetslipLimitationPopup();
            return;
        }
        const bbHashIds: string[] = this.storageService.get('bbHashIds');
        const bbIndex = bbHashIds?.indexOf(this.hashIdSignal());
        const betsAddedStorage = storedSelections?.map((item: IBetSelection) => item && item.outcomesIds?.[0]);
        if (this.initiator === this.FEATUREDMODULE) {
            this.betState = this.isHomeSLPButtonSelected(this.hashIdSignal());
        } else {
            this.betState = this.isSelected();
        }

        if (bbIndex === -1 && this.areSelectionsExistInBS(betsAddedStorage)) {
            //do nothing as these selections have already added to betslip
            return;
        }
        this.closePartialQuickBetAndAddSelectionToBetslip(this.betState);
        const isQuickbet = this.sessionStorage.get('showBetslip');
        if (!(storedSelections && storedSelections.length === 5) && !this.isDesktop && !isQuickbet) {
            this.pubSubService.publish(this.pubSubService.API.TOGGLE_LOADING_OVERLAY, {
                overlay: true,
                spinner: true,
            });
        }
    }

    /**
     * method for Check if all outcomes of the available markets exist in the storage.
     * @param bets: string[]
     * @returns {boolean}
     */
    private areSelectionsExistInBS(bets: string[]): boolean {
        return this.markets?.map((market: IMarket) => market?.outcomes?.[0]?.id).every((ocId: string) => bets?.includes(ocId));
    }

    /**
     * add to betslip on click acca bar
     * @param betState: boolean
     */
    private closePartialQuickBetAndAddSelectionToBetslip(betState: boolean): void {
        this.pubSubService.publishSync(this.pubSubService.API.CLOSE_QUICKBET_PARTIAL_OVERLAY);
        this.addLinesToBetSlip(betState);
    }

    /**
     * Adds lines to the bet slip based on the provided betState.
     * @param betState - Boolean
     */
    private addLinesToBetSlip(betState: boolean): void {
        const storedSelections = this.storageService.get('betSelections')?.map((item: IBetSelection) => item?.outcomesIds?.[0]) || [];
        const filteredSelections = this.filterSelections(storedSelections, betState);
        this.dataFormationBuildbet(filteredSelections);
        this.triggerBetslipUpdate();
    }

    /**
     * Methoid to filter selections if they exist in storage.
     * @param storedSelections: string[]
     * @param flag: boolean
     * @returns {IMarket[]}
     */
    private filterSelections(storedSelections: string[], flag: boolean): IMarket[] {
        return this.markets.filter((market: IMarket) =>
            flag ? storedSelections.includes(market.outcomes[0].id) : !storedSelections.includes(market.outcomes[0].id),
        );
    }

    /**
     * Method to Trigger betslip update with a delay
     */
    private triggerBetslipUpdate(): void {
        this.timer = this.windowRef.nativeWindow.setTimeout(() => {
            this.betslipSelectionsDataService.addToBetslip(this.preCreatedAccasBetsData, true);
            this.windowRef.nativeWindow.clearTimeout(this.timer);
        }, 150);
    }

    /**
     * data formation for build bet api payload
     * @returns { Array }
     */
    private dataFormationBuildbet(preCreatedData: IMarket[]): void {
        this.preCreatedAccasBetsData = [];
        const eventData = this.eventRefSignal();
        this.eventService.setBwinId(eventData!);
        preCreatedData.forEach((market: IMarket) => {
            let tracking: IGtmOrigin | null;
            let brandValue: string;
            const segment: string = this.routingState.getCurrentSegment();
            const outcome: IOutcome = market.outcomes[0];
            this.eventService.setBwinId(market);
            this.eventService.setBwinId(outcome);
            if (this.initiator === this.FEATUREDMODULE) {
                tracking = this.gtmTrackingService.detectTracking(GA_CONSTANTS.PBB_WIDGET, segment, eventData!, market);
                if (tracking) {
                    tracking.betPosition = { position: `Pre created - Bet Builder - ${preCreatedData.length}`, subTab: 'Pre created - Bet Builder' };
                }
                brandValue = GA_CONSTANTS.BRAND;
            } else {
                tracking = this.gtmTrackingService.detectTracking('', segment, eventData!, market, 'BET BUILDER - pre-built');
                if (tracking) {
                    tracking.betPosition = { position: `preCreated EDP - ${preCreatedData.length}`, subTab: 'Pre created - Bet Builder' };
                }
                brandValue = market.marketName || market.name;
            }
            const details = BetslipBetDataUtils.outcomeDetails(eventData!, market, outcome);
            const priceType = outcome?.prices?.length === 1 ? outcome?.prices?.[0].priceType : 'SP';
            const price: IPriceCore = { ...outcome.prices[0], ...(priceType && { priceType }) };
            const GTMObject: IAddToBetSlipGTMObject = {
                categoryID: String(eventData?.categoryId ?? ''),
                typeID: String(eventData?.typeId ?? ''),
                eventID: String(eventData?.id ?? ''),
                selectionID: String(outcome?.id),
            };
            const handicap: IHandicap = market.rawHandicapValue && {
                type: outcome.outcomeMeaningMajorCode,
                raw: outcome?.prices?.[0].handicapValueDec.replace(/,/g, ''),
            };
            if (tracking) {
                GTMObject['tracking'] = tracking;
                GTMObject['betData'] = {
                    name: this.removePipes(eventData?.name ?? ''),
                    category: String(eventData?.categoryId ?? ''),
                    variant: String(eventData?.typeId ?? ''),
                    brand: brandValue,
                    dimension60: String(eventData?.id ?? ''),
                    dimension61: outcome?.id,
                    dimension62: eventData?.eventIsLive ? 1 : 0,
                    dimension63: 0,
                    dimension64: tracking?.location,
                    dimension65: tracking?.module,
                    dimension94: this.eventIndex,
                };
            }
            const betSlipObject: IPreCreatedAddToBetSlipEventData = {
                isInsightsBet: true,
                eventIsLive: eventData?.eventIsLive ?? false,
                outcomes: [outcome],
                typeName: eventData?.typeName ?? '',
                price,
                handicap,
                goToBetslip: false,
                modifiedPrice: outcome.modifiedPrice,
                eventId: typeof eventData?.id === 'number' ? eventData.id : 0,
                bEid: eventData?.bwinId ?? '',
                bMid: market.bwinId,
                bSid: outcome.bwinId,
                bybAvailableEvent: true,
                bybAvailableMarket: true,
                categoryId: eventData?.categoryId ?? '',
                GTMObject: GTMObject,
                details,
                eventName: eventData?.name ?? '',
                isFeatured: eventData?.['@type'],
            };
            this.preCreatedAccasBetsData.push(betSlipObject);
        });
    }

    /**
     * Remove all the pipes for the event name
     * @param text
     * @returns string
     */
    removePipes(text: string): string {
        if (text) {
            return text.replace(/\|/g, '').trim();
        }
        return '';
    }

    ngOnDestroy(): void {
        this.pubSubService.unsubscribe(`preCreatedCard-${this.hashIdSignal()}`);
    }
}
